import { cn } from '@/lib/utils'
import type { CalendarEvent, CalendarProps, CalendarResource, PaymentStatus } from '@/lib/render'
import type { CalendarViewProps, TileMoveGeometry } from './shared'
import { EventShell } from './BookingActions'
import {
  AttendanceMark,
  CalendarNoteLayer,
  ClosedTimeLayer,
  columnOverlays,
  EmptyState,
  PaymentMark,
  deriveResources,
  durationMins,
  eventBelongsToResource,
  eventMatchesAnyResource,
  formatHourLabel,
  formatTime,
  getDatePart,
  getHours,
  minutesFromMidnight,
  parseHour,
  SlotBandLayer,
  statusColor,
  statusPrefix,
} from './shared'
import type { SlotPick } from '@/lib/slotBooking'

const RESOURCE_COL_WIDTH = 220
const TIME_COL_WIDTH = 54
// 1.5px/min — a 30-minute slot is 45px. Kept in step with MIN_EVENT_HEIGHT so a
// 30-min block fits title + customer + time without overlapping the next slot.
const PIXELS_PER_MINUTE = 1.5
// Floor for every event block so short appointments still fit title + customer + time.
const MIN_EVENT_HEIGHT = 44

interface PositionedEvent extends CalendarEvent {
  lane: number
  lanes: number
}

function positionOverlappingEvents(events: CalendarEvent[]): PositionedEvent[] {
  const sorted = [...events].sort((a, b) => a.start.localeCompare(b.start))
  const laneEnds: number[] = []

  return sorted.map(event => {
    const start = minutesFromMidnight(event.start)
    const end = minutesFromMidnight(event.end)
    let lane = laneEnds.findIndex(laneEnd => laneEnd <= start)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(end)
    } else {
      laneEnds[lane] = end
    }

    const concurrent = sorted.filter(other =>
      minutesFromMidnight(other.start) < end && minutesFromMidnight(other.end) > start,
    ).length

    return { ...event, lane, lanes: Math.max(concurrent, laneEnds.length) }
  })
}

function ResourceEventBlock({
  event,
  dayStart,
  interactive,
  onChanged,
  move,
}: {
  event: PositionedEvent
  dayStart: number
  interactive?: boolean
  onChanged?: () => void
  move?: TileMoveGeometry
}) {
  const color = statusColor(event)
  const cancelled = event.status === 'cancelled'
  const top = Math.max(0, (minutesFromMidnight(event.start) - dayStart) * PIXELS_PER_MINUTE)
  const height = Math.max(MIN_EVENT_HEIGHT, durationMins(event.start, event.end) * PIXELS_PER_MINUTE - 4)
  const gutter = 4
  const laneWidth = (RESOURCE_COL_WIDTH - 14 - gutter * Math.max(0, event.lanes - 1)) / event.lanes
  const left = 7 + event.lane * (laneWidth + gutter)

  return (
    <EventShell
      event={event}
      interactive={interactive}
      onChanged={onChanged}
      move={move}
      className={cn(
        'absolute flex flex-col overflow-hidden rounded-sm border-l-4 px-1.5 py-[3px]',
        cancelled && 'opacity-60',
      )}
      style={{
        backgroundColor: `${color}24`,
        borderLeftColor: color,
        top,
        height,
        left,
        width: laneWidth,
      }}
    >
      <div className="flex items-center justify-between gap-1">
        <span className={cn('min-w-0 flex-1 truncate text-[11px] leading-[13px] font-bold', cancelled && 'line-through')} style={{ color }}>{event.service_name}</span>
        <div className="flex items-center gap-[5px]">
          {/* Attendance was missing from this view entirely — the day grid showed
              a tick and this one did not, for the same booking. */}
          <AttendanceMark event={event} />
          {event.payment_status ? <PaymentMark status={event.payment_status as PaymentStatus} /> : null}
        </div>
      </div>
      {event.customer_name ? (
        <span className={cn('mt-px truncate text-[10px] leading-[12px] text-muted-foreground', cancelled && 'line-through')}>{event.customer_name}</span>
      ) : null}
      <span className={cn('mt-px truncate text-[9px] leading-[11px] text-muted-foreground', cancelled && 'line-through')}>
        {statusPrefix(event)}{formatTime(event.start)}
      </span>
    </EventShell>
  )
}

function ResourceColumn({
  resource,
  events,
  data,
  isFirstColumn,
  dayStart,
  dayEnd,
  height,
  interactive,
  onChanged,
  date,
  slotMinutes,
  onPickSlot,
  move,
}: {
  resource: CalendarResource
  events: CalendarEvent[]
  data: CalendarProps
  isFirstColumn: boolean
  dayStart: number
  dayEnd: number
  height: number
  interactive?: boolean
  onChanged?: () => void
  date: string
  slotMinutes: number
  onPickSlot?: (pick: SlotPick) => void
  move?: TileMoveGeometry
}) {
  const positioned = positionOverlappingEvents(events)
  // Same overlays as the day view — closures must not be visible on one day grid
  // and invisible on the other, and a band must never be offered over one here
  // either.
  const overlays = columnOverlays(data, resource, isFirstColumn)

  return (
    <div className="relative z-10 shrink-0 border-l border-border" style={{ width: RESOURCE_COL_WIDTH, height }}>
      <ClosedTimeLayer
        ranges={overlays.blocked}
        labels={overlays.blockLabels}
        dayStartMinutes={dayStart}
        pixelsPerMinute={PIXELS_PER_MINUTE}
      />
      {/* Bands next so the event blocks paint above them — see SlotBandLayer. */}
      {onPickSlot ? (
        <SlotBandLayer
          date={date}
          resource={resource}
          events={events}
          blocked={overlays.blocked}
          dayStartMinutes={dayStart}
          dayEndMinutes={dayEnd}
          intervalMinutes={slotMinutes}
          pixelsPerMinute={PIXELS_PER_MINUTE}
          onPickSlot={onPickSlot}
        />
      ) : null}
      {positioned.map(event => (
        <ResourceEventBlock
          key={`${resource.id}-${event.id}`}
          event={event}
          dayStart={dayStart}
          interactive={interactive}
          onChanged={onChanged}
          move={move}
        />
      ))}
      <CalendarNoteLayer
        notes={overlays.notes}
        dayStartMinutes={dayStart}
        pixelsPerMinute={PIXELS_PER_MINUTE}
      />
    </div>
  )
}

export function ResourceCalendarView({ data, interactive, onChanged, onPickSlot, onMoveBooking, showAllResources, hideDateHeading }: CalendarViewProps) {
  const allEvents = (data.events ?? [])
    .filter(event => getDatePart(event.start) === data.date)
    .sort((a, b) => a.start.localeCompare(b.start))
  const resources = deriveResources(allEvents, data.resources)
  const hours = getHours(data)
  const dayStart = parseHour(data.business_hours?.open, hours[0] ?? 8) * 60
  const dayEnd = parseHour(data.business_hours?.close, hours[hours.length - 1] ?? 20) * 60
  const gridHeight = Math.max(360, (dayEnd - dayStart) * PIXELS_PER_MINUTE)
  const dateLabel = new Date(`${data.date}T00:00:00`).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  // A wide-open day still needs its grid on a surface that can book into it.
  // Without any resource to draw there is genuinely nothing to show.
  if (allEvents.length === 0 && !(showAllResources && resources.length > 0)) {
    return (
      <div>
        {hideDateHeading ? null : (
          <h3 className="border-b border-border bg-muted px-4 py-2.5 text-[14px] font-bold text-foreground">{dateLabel}</h3>
        )}
        <EmptyState />
      </div>
    )
  }

  // Drag-to-move geometry for THIS grid's scale (1.5px/min — the day view draws
  // at 2.25, so a shared constant would land a drop on the wrong slot in one of
  // them).
  const move: TileMoveGeometry | undefined = onMoveBooking
    ? {
        pixelsPerMinute: PIXELS_PER_MINUTE,
        intervalMinutes: data.slot_minutes ?? 30,
        dayStartMinutes: dayStart,
        dayEndMinutes: dayEnd,
        date: data.date,
        onMoveBooking,
      }
    : undefined

  const hasUnassigned = resources.length > 0 && allEvents.some(event => !eventMatchesAnyResource(event, resources))
  const orderOf = (r: { display_order?: number }) => (typeof r.display_order === 'number' ? r.display_order : Number.POSITIVE_INFINITY)
  const orderedResources = [...resources].sort((a, b) => orderOf(a) - orderOf(b) || a.name.localeCompare(b.name))
  const visibleResources = resources.length
    ? [...orderedResources, ...(hasUnassigned ? [{ id: 'unassigned', name: 'Unassigned' }] : [])]
    : [{ id: 'unassigned', name: 'Unassigned' }]

  return (
    <div>
      {hideDateHeading ? null : (
        <h3 className="border-b border-border bg-muted px-4 py-2.5 text-[14px] font-bold text-foreground">{dateLabel}</h3>
      )}
      {/* ONE scroll container for both axes — sticky answers only to the nearest
          scrolling ancestor, so the frozen time gutter and header need both
          scrollbars on the same element. Same arrangement as DayCalendarView. */}
      <div className="max-h-[680px] overflow-auto">
        <div className="w-max">
          <div className="sticky top-0 z-30 flex h-[54px] border-b border-border bg-card">
            {/* Corner cell: sticky left too, so neither scroll axis moves it. */}
            <div className="sticky left-0 z-10 shrink-0 bg-card" style={{ width: TIME_COL_WIDTH }} />
            {visibleResources.map((resource, index) => (
              <div
                key={resource.id}
                className={cn(
                  'flex shrink-0 flex-col items-center justify-center border-l border-border px-2',
                  index === 0 && 'bg-muted',
                )}
                style={{ width: RESOURCE_COL_WIDTH }}
              >
                <span className="line-clamp-2 text-center text-[13px] font-semibold text-foreground">{resource.name}</span>
              </div>
            ))}
          </div>
          <div className="relative flex bg-card" style={{ height: gridHeight }}>
            {/* Grid lines first so they paint UNDER the event blocks, never across them. */}
            {/* z-0 (columns sit at z-10) so the grid never draws across an event block —
                sibling order alone is not enough on Android. */}
            {hours.map(hour => (
              <div
                key={`line-${hour}`}
                className="pointer-events-none absolute z-0 h-px bg-border"
                style={{
                  top: (hour * 60 - dayStart) * PIXELS_PER_MINUTE,
                  left: TIME_COL_WIDTH,
                  width: visibleResources.length * RESOURCE_COL_WIDTH,
                }}
              />
            ))}
            {/* z-20 beats the z-10 columns so event blocks slide UNDER the
                frozen gutter; the opaque bg-card is what hides them. */}
            <div className="sticky left-0 z-20 shrink-0 bg-card" style={{ width: TIME_COL_WIDTH, height: gridHeight }}>
              {hours.map(hour => (
                <span
                  key={hour}
                  className="absolute right-2 whitespace-nowrap text-[10px] text-muted-foreground"
                  style={{ top: (hour * 60 - dayStart) * PIXELS_PER_MINUTE - 7 }}
                >
                  {formatHourLabel(hour)}
                </span>
              ))}
            </div>
            {visibleResources.map((resource, columnIndex) => {
              const resourceEvents = resource.id === 'unassigned' && resources.length > 0
                ? allEvents.filter(event => !eventMatchesAnyResource(event, resources))
                : resource.id === 'unassigned'
                  ? allEvents
                : allEvents.filter(event => eventBelongsToResource(event, resource))

              return (
                <ResourceColumn
                  key={resource.id}
                  resource={resource}
                  events={resourceEvents}
                  data={data}
                  isFirstColumn={columnIndex === 0}
                  dayStart={dayStart}
                  dayEnd={dayEnd}
                  height={gridHeight}
                  interactive={interactive}
                  onChanged={onChanged}
                  date={data.date}
                  slotMinutes={data.slot_minutes ?? 30}
                  onPickSlot={onPickSlot}
                  move={move}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
